import { Grid, Stack } from "@mui/material";
import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { Checkbox } from "@/modules/irohana-components/elements/Checkbox";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { Select } from "@/modules/irohana-components/elements/Select";
import { BILLING_MONTH_OPTIONS, SELECT_PLACEHOLDER } from "@/features/organization/invoice/constants/invoiceEnums";
import { TInvoiceForm } from "@/features/organization/invoice/types/InvoiceForm";

type TDayFieldProps = {
  dayName: "billingDay" | "paymentCutoffDay";
  lastDayName: "billingDayLastDay" | "paymentCutoffLastDay";
  label: string;
};

/**
 * 請求日 / 支払締切（日）: editable 1〜29 when 末日に設定 is off; when on, the day is auto-filled
 * (last day of month) and the input becomes read-only (spec No.31 / No.34).
 */
const DayField = ({ dayName, lastDayName, label }: TDayFieldProps) => {
  const { control, setValue } = useFormContext<TInvoiceForm>();
  const isLastDay = useWatch({ control, name: lastDayName });

  // Checking 末日 clears any typed day (spec No.32 / No.35): the last day is auto-resolved at billing time.
  const handleLastDayChange = (_event: unknown, checked: boolean) => {
    if (checked) setValue(dayName, "", { shouldValidate: true, shouldDirty: true });
  };

  return (
    <Stack gap={1}>
      <FormField control={control} name={dayName} label={label} required>
        <Input
          readonly={isLastDay}
          placeholder={isLastDay ? "自動入力内容 (readonly)" : "1〜29"}
          endAdornment="日"
          clearable={false}
          pattern={/^\d*$/}
          inputProps={{ maxLength: 2, inputMode: "numeric" }}
        />
      </FormField>
      <FormField control={control} name={lastDayName} noLabel>
        <Checkbox label="末日に設定" onChange={handleLastDayChange} />
      </FormField>
    </Stack>
  );
};

/** 請求期日情報 — 請求月/請求日 と 支払締切（月）/支払締切（日）. */
export const BillingDueSection = memo(() => {
  const { control } = useFormContext<TInvoiceForm>();

  return (
    <CollapsibleCard title="請求期日情報">
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={12} md={6}>
          <FormField control={control} name="billingMonth" label="請求月" required>
            <Select options={BILLING_MONTH_OPTIONS} placeholder={SELECT_PLACEHOLDER} clearable />
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <DayField dayName="billingDay" lastDayName="billingDayLastDay" label="請求日" />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField control={control} name="paymentCutoffMonth" label="支払締切（月）" required>
            <Select options={BILLING_MONTH_OPTIONS} placeholder={SELECT_PLACEHOLDER} clearable />
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <DayField dayName="paymentCutoffDay" lastDayName="paymentCutoffLastDay" label="支払締切（日）" />
        </Grid>
      </Grid>
    </CollapsibleCard>
  );
});

BillingDueSection.displayName = "BillingDueSection";
